import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  applyDocumentLanguage,
  applyDomTranslations,
  COMMON_TRANSLATIONS,
  formatLocalizedDate,
  getInitialLanguage,
  getLanguageOption,
  LANGUAGES,
  persistLanguage,
  translateText,
  withLanguageParam,
} from '../../../shared/localization/core'
import type { LanguageCode, LanguageOption, SiteMeta } from '../../../shared/localization/core'

interface LanguageContextValue {
  language: LanguageCode
  languageOption: LanguageOption
  languages: LanguageOption[]
  setLanguage: (language: LanguageCode) => void
  t: (text: string) => string
  apiPath: (path: string) => string
  formatDate: (value: string | undefined) => string
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

export const kf1Meta: SiteMeta = {
  title: {
    en: 'Uliunai Killing Floor 1',
    lt: 'Uliunai Killing Floor 1',
    ru: 'Uliunai Killing Floor 1',
    fr: 'Uliunai Killing Floor 1',
    de: 'Uliunai Killing Floor 1',
  },
  description: {
    en: 'Killing Floor 1 community server, stats, perks, VIP, and updates.',
    lt: 'Killing Floor 1 bendruomenės serveris, statistika, perkai, VIP ir naujienos.',
    ru: 'Сервер сообщества Killing Floor 1, статистика, перки, VIP и новости.',
    fr: 'Serveur communautaire Killing Floor 1, stats, atouts, VIP et actualités.',
    de: 'Killing Floor 1 Community-Server, Stats, Perks, VIP und Updates.',
  },
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => getInitialLanguage())

  const value = useMemo<LanguageContextValue>(() => {
    const setLanguage = (next: LanguageCode) => {
      persistLanguage(next)
      setLanguageState(next)
    }

    return {
      language,
      languageOption: getLanguageOption(language),
      languages: LANGUAGES,
      setLanguage,
      t: (text: string) => translateText(text, language, COMMON_TRANSLATIONS),
      apiPath: (path: string) => withLanguageParam(path, language),
      formatDate: (value: string | undefined) => formatLocalizedDate(value, language),
    }
  }, [language])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) throw new Error('useLanguage must be used inside LanguageProvider')
  return context
}

export function LocalizationRuntime({ meta }: { meta: SiteMeta }) {
  const { language } = useLanguage()

  useEffect(() => {
    applyDocumentLanguage(language, meta)

    const root = document.getElementById('root') ?? document.body
    const apply = () => applyDomTranslations(root, language, COMMON_TRANSLATIONS)
    apply()

    let frame: number | null = null
    const observer = new MutationObserver(() => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        apply()
      })
    })

    observer.observe(root, { childList: true, subtree: true, characterData: true })

    return () => {
      observer.disconnect()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [language, meta])

  return null
}

